import * as Sentry from '@sentry/react';

/**
 * Sentry for the business admin app (M14.5, ADR-020, SECURITY.md §8, §10).
 *
 * Identical rules to the patient app's, for a different reason: this app does not
 * *collect* PHI, it *displays* it. The appointment list, the patient record and the
 * intake viewer are all on screen for most of a working day, so:
 *
 * - **No Session Replay.** Not configured, not lazily loaded, not sampled at zero - the
 *   integration is absent. A replay of a reception screen is a recording of twenty-five
 *   patients (ADR-020), and a masking option that can be turned off is not a control.
 * - **`sendDefaultPii: false`**, so no ip address and no cookie reaches an envelope.
 * - **URLs are stripped to their path**, because an admin route carries a patient id in
 *   the path and a search term in the query, and the search term is a patient's name.
 * - **No automatic breadcrumbs for console or DOM input.** The DOM integration would
 *   record what a receptionist typed into the patient search box.
 *
 * Nothing runs at all unless `VITE_SENTRY_DSN` is set, which it is not in development
 * and not in the mock-driven builds.
 *
 * The scrubber below is deliberately a copy of the *shape* of the API's, not an import
 * of it: `apps/api/src/core/sentry-scrub.ts` imports the server's pino denylist and
 * `node:crypto`, and pulling it into a browser bundle would drag both across. The two
 * lists are checked against each other in `Docs/SECURITY.md` §8's table rather than by
 * a shared package no third thing would use.
 */

/** Field names removed wherever they appear, mirroring the API's denylist. */
const REDACTED_KEYS = new Set(
  [
    'password',
    'token',
    'secret',
    'apiKey',
    'credentials',
    'authorization',
    'cookie',
    'nonce',
    'card',
    'cardNumber',
    'pan',
    'cvv',
    'ssn',
    'memberId',
    'groupNumber',
    'dateOfBirth',
    'dob',
    'firstName',
    'lastName',
    'name',
    'email',
    'phone',
    'address',
    'addressLine1',
    'addressLine2',
    'city',
    'postalCode',
    'zip',
    'answers',
    'note',
    'notes',
    'reasonForVisit',
  ].map((key) => key.toLowerCase()),
);

const CENSOR = '[redacted]';

const VALUE_PATTERNS: readonly RegExp[] = [
  /[\w.+-]+@[\w-]+\.[\w.-]+/g,
  /\b\d{3}-\d{2}-\d{4}\b/g,
  /\b(?:\d[ -]?){13,19}\b/g,
  /\+\d{10,15}\b/g,
];

function scrubString(value: string): string {
  let out = value;
  for (const pattern of VALUE_PATTERNS) out = out.replace(pattern, CENSOR);
  return out;
}

function redactDeep(value: unknown, depth = 0): unknown {
  if (depth > 12) return CENSOR;
  if (typeof value === 'string') return scrubString(value);
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map((entry) => redactDeep(entry, depth + 1));

  const out: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
    out[key] = REDACTED_KEYS.has(key.toLowerCase()) ? CENSOR : redactDeep(entry, depth + 1);
  }
  return out;
}

function stripQuery(url: string): string {
  const cut = url.search(/[?#]/);
  return cut === -1 ? url : url.slice(0, cut);
}

/** The `beforeSend` of SECURITY.md §10, applied to a browser event. */
export function scrubBrowserEvent(event: Record<string, unknown>): Record<string, unknown> {
  // The user: an id hash at most. The apps never set one today, so this is the rule
  // that keeps a future `setUser({ email })` from becoming a leak.
  const user = event['user'] as Record<string, unknown> | undefined;
  if (user !== undefined && user !== null) {
    const id = user['id'];
    event['user'] = typeof id === 'string' ? { id } : {};
  }

  const request = event['request'] as Record<string, unknown> | undefined;
  if (request !== undefined && request !== null) {
    const url = typeof request['url'] === 'string' ? stripQuery(request['url']) : undefined;
    event['request'] = url === undefined ? {} : { url };
  }

  if (typeof event['message'] === 'string') event['message'] = scrubString(event['message']);
  if (event['extra'] !== undefined) event['extra'] = redactDeep(event['extra']);
  if (event['contexts'] !== undefined) event['contexts'] = redactDeep(event['contexts']);
  if (event['tags'] !== undefined) event['tags'] = redactDeep(event['tags']);

  const breadcrumbs = event['breadcrumbs'];
  if (Array.isArray(breadcrumbs)) {
    event['breadcrumbs'] = breadcrumbs.map((crumb: Record<string, unknown>) => {
      const out = { ...crumb };
      if (typeof out['message'] === 'string') out['message'] = scrubString(out['message']);
      if (out['data'] !== undefined) out['data'] = redactDeep(out['data']);
      return out;
    });
  }

  return event;
}

/**
 * Initialises Sentry when a DSN is configured. Called once from `main.tsx`, before the
 * React tree mounts, so an error during the first render is still captured.
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (dsn === undefined || dsn.length === 0) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE,
    // The same string the footer shows and `/version` returns, inlined by vite.config.ts.
    release: __APP_VERSION__,
    sendDefaultPii: false,
    // Low, because tracing here buys route timings that Azure Monitor already has from
    // the edge, and every transaction is one more object to scrub (ENVIRONMENT.md §1.1).
    tracesSampleRate: 0.02,
    // The default integration list minus the three that record what the patient sees or
    // types. Session Replay is not in this list and must never be added: see the note
    // at the top of this file and ADR-020.
    integrations: (defaults) =>
      defaults.filter(
        (integration) =>
          integration.name !== 'Breadcrumbs' &&
          integration.name !== 'Replay' &&
          integration.name !== 'ReplayCanvas',
      ),
    beforeSend: (event) => scrubBrowserEvent(event as unknown as Record<string, unknown>) as never,
  });
}
